// claims.ts — who holds what.
//
// Local, client-side, and deliberately small. atlas-00 §1.9 defers game state
// to Phase 4 and §10 anti-scopes wallets, chains and accounts; none of that is
// here. What IS here is the SHAPE of the data, so Phase 4 has somewhere to
// land: a plot, a holder, a moment, keyed by ground rather than by lattice.
//
// Keyed by the plot's quantised address rather than its lattice coordinate,
// because a lattice coordinate is relative to one bake. Re-cut a site with a
// different radius and every (q, r) means something else, while the ground —
// and therefore the claim on it — has not moved.

import { plotAddress } from './plotName.ts';
import { createStoredRecord } from './storedRecord.ts';
import { wrap180HalfOpen } from '../math/angles.ts';

const STORAGE_KEY = 'atlas.claims.v1';

export interface Claim {
  /** "Spiny Snarl Emu" — stored alongside the cell so a claim stays legible
   *  without needing the codec to read it back. */
  name: string;
  /** H3 res-8 cell. The identifier MoM titles are keyed by. */
  cell: string;
  holder: string;
  /**
   * The sol within the Atlas's own mission clock, at the moment of staking.
   * There is no era clock anywhere in the engine, so era is not a field here -
   * inventing one would be exactly the fiction this shape exists to avoid.
   */
  sol: number;
  /** Wall-clock, for ordering a player's own history. */
  at: number;
  lat: number;
  lon: number;
  /**
   * The body this ground is on. A claim staked before this field existed has
   * none, and is not on any particular body as far as a reader can tell - a
   * cell id alone does not say, since H3 addresses a unit sphere the same way
   * for every body and the same cell can fall on two different ones.
   */
  bodyId?: string;
}

/**
 * The holder every claim staked from this browser carries.
 *
 * The server files a claim under the crew's session subject, which is not a
 * name to show a player, and a `GET /api/craft/state` answer only ever
 * reports this crew's own rows - so a row read back from it is this ship's.
 */
export const CREW_HOLDER = 'CCS Tractatus';

type ClaimMap = Record<string, Claim>;

/** Bumped whenever the claimed set could have moved, so a per-frame consumer
 *  can tell without walking it. */
let version = 0;

const store = createStoredRecord<Claim>(STORAGE_KEY, () => {
  aggCache.clear();
  version++;
});
const { load, save } = store;

/**
 * The key a claim is filed under: the H3 resolution-8 cell.
 *
 * This is the same identifier MoM has used for plot ownership since 2021, which
 * makes it the right seam for Phase 4 — a claim recorded here is already keyed
 * the way a real title would be, rather than by a quantised coordinate of my
 * own devising.
 */
export function claimKey(latDeg: number, lonDeg: number): string {
  return plotAddress(latDeg, lonDeg).cell;
}

export function getClaim(latDeg: number, lonDeg: number): Claim | null {
  return load()[claimKey(latDeg, lonDeg)] ?? null;
}

/** The claim over a cell already in hand, without going back through h3. */
export function getClaimByCell(cell: string): Claim | null {
  return load()[cell] ?? null;
}

/**
 * Overwrites the whole cache with the server's answer.
 *
 * The server owns claims now (`server/engine/claims.ts`), so this store is a
 * cache and this is the only thing that makes it agree: a row the server does
 * not report is gone, whatever this browser still remembers about it.
 */
export function replaceAllClaims(next: readonly Claim[]): void {
  const m: ClaimMap = {};
  for (const c of next) m[c.cell] = c;
  save(m);
}

export function setClaim(
  latDeg: number,
  lonDeg: number,
  holder: string,
  sol: number,
  bodyId: string,
): Claim {
  const m = load();
  const addr = plotAddress(latDeg, lonDeg);
  const claim: Claim = {
    name: addr.name,
    cell: addr.cell,
    holder,
    sol,
    at: Date.now(),
    lat: latDeg,
    lon: lonDeg,
    bodyId,
  };
  m[claimKey(latDeg, lonDeg)] = claim;
  save(m);
  return claim;
}

export function releaseClaim(latDeg: number, lonDeg: number): void {
  const m = load();
  delete m[claimKey(latDeg, lonDeg)];
  save(m);
}

export function allClaims(): Claim[] {
  return Object.values(load()).sort((a, b) => b.at - a.at);
}

// -----------------------------------------------------------------------------
// H3 aggregation - the claim overlay's ground-based texture reads this, not
// the per-plot lattice `getClaim` above.
//
// Stored claims are keyed at res-8, MoM's own parcel; the overlay never draws
// finer than a body's registry resolution (`hexOverlay.ts`), which is coarser
// on every body in the registry. `claimedCellsAt` is where the two meet:
// aggregate every stored key up to whichever resolution altitude selected.
// -----------------------------------------------------------------------------

type H3 = typeof import('h3-js');
let h3: H3 | null = null;
let h3Loading = false;

/** Lazy, like every other module that touches h3-js - plotName.ts, hexOverlay.ts. */
function ensureH3(): void {
  if (h3 || h3Loading) return;
  h3Loading = true;
  void import('h3-js').then((m) => { h3 = m; version++; });
}

interface ClaimsDebug {
  /** Res-8 cells standing in for the real store; null defers to `load()`. */
  seed: readonly string[] | null;
  /** `--inject claim-shift`: every aggregated cell reads as one of its own neighbours. */
  shift: boolean;
}

const debug: ClaimsDebug = { seed: null, shift: false };

/** `descent-dojo --hex --claims` / `--inject claim-shift`. */
export function setClaimsDebug(next: { seed?: readonly string[] | null; shift?: boolean }): void {
  if (next.seed !== undefined) debug.seed = next.seed;
  if (next.shift !== undefined) debug.shift = next.shift;
  aggCache.clear();
  version++;
}

export function claimsDebug(): { seed: readonly string[] | null; shift: boolean } {
  return { seed: debug.seed, shift: debug.shift };
}

/** Which revision of the claimed set `claimedCellsAt` would answer from. */
export function claimsVersion(): number {
  return version;
}

/** `claimedCellsAt`'s own memo, cleared whenever the source data changes. */
const aggCache = new Map<number, Set<string>>();

// A claim staked in ANOTHER tab. `storage` never fires in the document that
// made the change, so this is the cross-tab half of what `save()` does for
// the tab that wrote: without it a per-frame consumer reading
// `claimsVersion()` never learns the store moved, since nothing on that path
// calls `load()` itself.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key !== null && e.key !== STORAGE_KEY) return;
    store.invalidate();
    aggCache.clear();
    version++;
  });
}

/**
 * Which cells at `res` are claimed, aggregating the res-8 store upward.
 *
 * Memoised per resolution: a tile asks this once per decode, and the
 * aggregation - one `cellToParent` per stored key - must not run per texel.
 * Empty (not an error) until h3-js has loaded, the same as `hexOverlay`
 * drawing nothing until then.
 */
export function claimedCellsAt(res: number): Set<string> {
  ensureH3();
  const lib = h3;
  if (!lib) return new Set();
  // Before the memo, not after: `load()` is the only thing that notices a
  // write this tab did not make, and it clears the memo when it does.
  const stored = load();
  const cached = aggCache.get(res);
  if (cached) return cached;
  const keys = debug.seed ?? Object.keys(stored);
  let out = new Set<string>();
  for (const key of keys) {
    try {
      out.add(lib.cellToParent(key, res));
    } catch {
      /* malformed or out-of-range key - skip rather than fail the whole set */
    }
  }
  if (debug.shift) {
    const shifted = new Set<string>();
    for (const cell of out) {
      try {
        const ring = lib.gridDisk(cell, 1);
        shifted.add(ring[1] ?? cell);
      } catch {
        shifted.add(cell);
      }
    }
    out = shifted;
  }
  aggCache.set(res, out);
  return out;
}

/** The cell containing a ground point at `res`, or null before h3-js loads. */
export function cellAt(latDeg: number, lonDeg: number, res: number): string | null {
  ensureH3();
  if (!h3) return null;
  const lon = wrap180HalfOpen(lonDeg);
  try {
    return h3.latLngToCell(latDeg, lon, res);
  } catch {
    return null;
  }
}

/**
 * The claimed set at `res` as an equirectangular R8 mask, 255 claimed.
 *
 * The tile decode bakes a claim texture per tile, which is the right answer
 * wherever there IS a tile - and above a body's ORBIT band edge there is none
 * (`tilesActive` in src/atlas/pyramidView.ts), so from up there the globe is the ground and
 * needs the wash itself. Built per CLAIMED CELL rather than per texel: the cost
 * follows the claimed area, not the texture, so a body with three holdings does
 * not pay for half a million `latLngToCell` calls.
 *
 * Null before h3-js loads, and null with nothing claimed, so a caller can bind
 * an empty texture rather than an all-zero half megabyte.
 */
export function claimedEquirect(
  res: number, width: number, height: number,
): Uint8Array<ArrayBuffer> | null {
  ensureH3();
  const lib = h3;
  if (!lib) return null;
  const cells = claimedCellsAt(res);
  if (cells.size === 0) return null;
  const data = new Uint8Array(width * height);
  for (const cell of cells) {
    let ring: number[][];
    try {
      ring = lib.cellToBoundary(cell);
    } catch {
      continue;
    }
    const first = ring[0];
    if (first === undefined) continue;
    const lon0 = first[1] ?? 0;
    let latMin = 90;
    let latMax = -90;
    let dMin = 0;
    let dMax = 0;
    for (const v of ring) {
      const la = v[0] ?? 0;
      const d = wrap180HalfOpen((v[1] ?? 0) - lon0);
      if (la < latMin) latMin = la;
      if (la > latMax) latMax = la;
      if (d < dMin) dMin = d;
      if (d > dMax) dMax = d;
    }
    // A cell over a pole spans every longitude and its boundary does not say
    // so: the ring walks round the pole and reads as a narrow strip.
    const wide = dMax - dMin > 90 || latMax > 89.5 || latMin < -89.5;
    const j0 = Math.max(0, Math.floor(((latMin + 90) / 180) * height) - 1);
    const j1 = Math.min(height - 1, Math.ceil(((latMax + 90) / 180) * height));
    const i0 = wide ? 0 : Math.floor(((lon0 + dMin + 180) / 360) * width) - 1;
    const i1 = wide ? width - 1 : Math.ceil(((lon0 + dMax + 180) / 360) * width);
    for (let j = j0; j <= j1; j++) {
      const lat = ((j + 0.5) / height) * 180 - 90;
      for (let k = i0; k <= i1; k++) {
        const i = ((k % width) + width) % width;
        const lon = ((i + 0.5) / width) * 360 - 180;
        try {
          if (lib.latLngToCell(lat, lon, res) === cell) data[j * width + i] = 255;
        } catch {
          /* a texel h3 will not address - the poles at some resolutions */
        }
      }
    }
  }
  return data;
}

